import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLeftMenuItem, getLeftMenus, updateLeftMenuItem } from '../../../../api/managerApi';
import '../leftMenu.css';

const validatePathActionUrl = (path, action, url) => {
  const hasPath = Boolean(String(path || '').trim());
  const hasAction = Boolean(String(action || '').trim());
  const hasUrl = Boolean(String(url || '').trim());
  const filledCount = [hasPath, hasAction, hasUrl].filter(Boolean).length;

  if (filledCount === 0) {
    return 'path, url, action 중 하나는 반드시 입력해야 합니다.';
  }

  if (filledCount > 1) {
    return 'path, url, action은 동시에 입력할 수 없습니다. 하나만 입력하세요.';
  }

  return null;
};

function LeftMenuItemEdit({ itemId, onSuccess, onCancel }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [categoryMenus, setCategoryMenus] = useState([]);
  const [validationMessage, setValidationMessage] = useState('');

  useEffect(() => {
    if (!itemId) return;
    setLoading(true);
    Promise.all([getLeftMenuItem(itemId), getLeftMenus()])
      .then(([itemData, menuRows]) => {
        const categories = (Array.isArray(menuRows) ? menuRows : []).filter((menu) => menu.type === 'category');
        setCategoryMenus(categories);

        const currentMenuId = String(itemData.menu_id);
        const hasCurrent = categories.some((menu) => String(menu.menu_id) === currentMenuId);
        const fallbackMenuId = categories[0] ? String(categories[0].menu_id) : '';

        setForm({
          ...itemData,
          menu_id: hasCurrent ? currentMenuId : fallbackMenuId,
          is_active: String(Boolean(itemData.is_active)),
        });
      })
      .catch(err => {
        console.error('하위메뉴/카테고리 메뉴 조회 실패:', err);
        alert('하위메뉴 조회 실패');
      })
      .finally(() => setLoading(false));
  }, [itemId]);

  const selectedMenuLabel = useMemo(() => {
    if (!form?.menu_id) return '-';
    const selected = categoryMenus.find((menu) => String(menu.menu_id) === String(form.menu_id));
    return selected?.label || '-';
  }, [categoryMenus, form?.menu_id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setValidationMessage('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!itemId) return;

    const pathActionError = validatePathActionUrl(form.path, form.action, form.url);
    if (pathActionError) {
      setValidationMessage(pathActionError);
      alert(pathActionError);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        menu_id: Number(form.menu_id),
        sort_order: Number(form.sort_order || 0),
        is_active: String(form.is_active) === 'true',
      };
      const updated = await updateLeftMenuItem(itemId, payload);
      if (onSuccess) {
        onSuccess(updated);
      } else {
        navigate('/manager/left-menu');
      }
    } catch (err) {
      console.error('하위메뉴 수정 실패:', err);
      alert('하위메뉴 수정 실패');
    } finally {
      setSaving(false);
    }
  };

  if (!itemId) return <div className="mgr-loading">수정할 하위메뉴가 선택되지 않았습니다.</div>;
  if (loading) return <div className="mgr-loading">로딩 중...</div>;
  if (!form) return <div className="mgr-loading">하위메뉴 데이터가 없습니다.</div>;

  return (
    <>
      <div className="mgr-panel-header">
        <div>
          <div className="mgr-panel-title">하위메뉴 수정</div>
          <div className="mgr-panel-subtitle">상위 메뉴: {selectedMenuLabel}</div>
        </div>
      </div>
      <div className="mgr-panel-body">
        <form onSubmit={handleSubmit}>
          <div className="mgr-section">
            <div className="mgr-section-title">기본 정보</div>
            <div className="mgr-section-body">
              <div className="row g-2">
                <div className="col-md-5">
                  <label className="form-label">상위 메뉴</label>
                  <select className="form-select form-select-sm" name="menu_id" value={form.menu_id || ''} onChange={handleChange} required>
                    <option value="">카테고리 메뉴 선택</option>
                    {categoryMenus.map((menu) => (
                      <option key={menu.menu_id} value={menu.menu_id}>
                        {menu.label} ({menu.menu_key})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-7">
                  <label className="form-label">라벨 (label)</label>
                  <input className="form-control form-control-sm" name="label" value={form.label || ''} onChange={handleChange} required />
                </div>
              </div>
            </div>
          </div>

          <div className="mgr-section">
            <div className="mgr-section-title">
              경로 설정&nbsp;
              <span className="text-muted fw-normal" style={{ textTransform: 'none', letterSpacing: 0 }}>— path, url, action 중 하나만 입력</span>
            </div>
            <div className="mgr-section-body">
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">path</label>
                  <input className="form-control form-control-sm" name="path" value={form.path || ''} onChange={handleChange} placeholder="/example" />
                </div>
                <div className="col-md-6">
                  <label className="form-label">action</label>
                  <input className="form-control form-control-sm" name="action" value={form.action || ''} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">url</label>
                  <input className="form-control form-control-sm" name="url" value={form.url || ''} onChange={handleChange} placeholder="https://..." />
                </div>
              </div>
              {validationMessage && <div className="text-danger small mt-2">{validationMessage}</div>}
            </div>
          </div>

          <div className="mgr-section">
            <div className="mgr-section-title">기타 설정</div>
            <div className="mgr-section-body">
              <div className="row g-2">
                <div className="col-md-3">
                  <label className="form-label">정렬 순서</label>
                  <input className="form-control form-control-sm" name="sort_order" type="number" value={form.sort_order ?? 0} onChange={handleChange} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">활성 상태</label>
                  <select className="form-select form-select-sm" name="is_active" value={String(form.is_active)} onChange={handleChange}>
                    <option value="true">활성</option>
                    <option value="false">비활성</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div className="mgr-form-footer">
        <button className="btn btn-primary btn-sm" onClick={handleSubmit} disabled={saving}>{saving ? '저장중...' : '저장'}</button>
        <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => (onCancel ? onCancel() : navigate(-1))}>취소</button>
      </div>
    </>
  );
}

export default LeftMenuItemEdit;
